/**
 * Location epoch helpers.
 *
 * The epoch is a monotonic counter in `state.location.locationEpoch` that
 * bumps whenever `currentLocationId` transitions to a different value (and
 * on logout). Callers capture the value before an await and drop the write
 * if it no longer matches at resolve time.
 *
 * Prevents late responses from a previous location silently merging into
 * the current-location Redux caches (product/order slices are merge-on-write).
 *
 * Import lazily to avoid any accidental circular between this helper and
 * anything that Store transitively loads.
 */

// eslint-disable-next-line @typescript-eslint/no-var-requires
const getStore = () => require('../redux/Store').default as {
  getState: () => { location: { locationEpoch: number } };
};

export function getLocationEpoch(): number {
  try {
    // Coerce undefined (persisted state predating this field) to 0 so
    // captured epochs and comparisons are always finite numbers.
    const raw = getStore().getState().location.locationEpoch;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
  } catch {
    return 0;
  }
}

export function isCurrentLocationEpoch(epoch: number): boolean {
  return getLocationEpoch() === epoch;
}
